import {
  Autocomplete,
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { SetStateAction, useEffect, useState } from "react";
import * as Yup from "yup";

import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker as MuiDatePicker } from "@mui/x-date-pickers/DatePicker";

import { useSubscription, useUserInfo } from "store/auth/auth-store";
import { useTranslation } from "react-i18next";
import { useSOWSbySupplierCode } from "services/input-core/sows/get-sows-by-supplier-code";

import { useSuppliersQuery } from "services/input-core/suppliers/get-all-suppliers";
import { useWorkersByVendorCode } from "services/input-core/workers/get-workers-by-vendor-code copy";
import Skeleton from "react-loading-skeleton";
import { addMonths, format } from "date-fns";
import ButtonTS from "components/Forms/CustomButtons/ButtonTS";
import { useGetReportsAttachmentFiles } from "services/input-core/reports/get-reports-attachment-files";
import { GetReportsAttachmentFilesPayload } from "services/input-core/reports/@types";
import { checkMessageErrors } from "utils/functions";
import { toast } from "react-toastify";
import { useReportsContext } from "store/reports/reportsContext";

export function FilesReport() {
  const { t } = useTranslation(["files-reports-messages", "list-errors"]);
  const { subscription } = useSubscription();
  const { 
    isLoadingReport, 
    setIsLoadingReport, 
    socketNotification 
  } = useReportsContext();

  const [selectedOption, setSelectedOption] = useState("sows");
  const [initialDate, setInitialDate] = useState<Date>(new Date());
  const [endDate, setEndDate] = useState<Date>(addMonths(new Date(), 3));
  const [workersFromSow, setWorkersFromSow] = useState(false);
  const [supplier, setSupplier] = useState(undefined);
  const [fieldglassId, setFieldglassId] = useState(undefined);
  const [loading, setLoading] = useState(false);

  const { data: suppliers, isLoading: isLoadingSuppliers } = useSuppliersQuery(
    subscription?.subscriptionId,
  );

  const { data: Sows, isLoading: isSowsLoading } = useSOWSbySupplierCode(
    supplier?.supplierCode,
    {
      isDropdown: true,
    },
    {
      enabled: !!supplier?.supplierCode,
    },
  );

  const { data: Workers, isLoading: isWorkersLoading } = useWorkersByVendorCode(
    supplier?.supplierCode,
    {
      enabled: !!supplier?.supplierCode,
    },
  );

  const { mutate: getReportsAttachmentFiles } = useGetReportsAttachmentFiles();

  const handleRadioChange = (event: {
    target: { value: SetStateAction<string> };
  }) => {
    setFieldglassId(undefined);
    setSelectedOption(event.target.value);
  };

  const handleSendButton = async () => {
    try {
      const schema = Yup.object({
        supplier: Yup.string().required(t("supplier-code")),
        endDate: Yup.date().required(t("end-date")),
        initialDate: Yup.date().required(t("start-date")),
      });

      await schema.validate({
        initialDate,
        endDate,
        supplier: supplier?.supplierCode ?? "",
      });

      const params: GetReportsAttachmentFilesPayload = {
        subscription: subscription?.subscriptionId,
        fieldglassId: fieldglassId
          ? fieldglassId.workerID ?? fieldglassId.sowID
          : "",
        startDateString: format(initialDate, "yyyy-MM-dd"),
        endDateString: format(endDate, "yyyy-MM-dd"),
        sowWorkers: selectedOption === "sows" && workersFromSow,
        supplierCode: supplier.supplierCode,
      };

      setIsLoadingReport(true);
      setLoading(true);

      getReportsAttachmentFiles([params], {
        onSuccess() {
          socketNotification();
          toast.info(t("report-processing"));
          setLoading(false);
        },
        onError(error: any) {
          toast.error(
            t(
              checkMessageErrors(
                error?.response?.data?.error,
                "ERR_FILES_NOT_FOUND_DEFAULT",
              ),
              { ns: "list-errors" },
            ),
          );
          setIsLoadingReport(false);
          setLoading(false);
        },
      });
    } catch (validationError) {
      toast.error(validationError.message);
      setLoading(false);
    }
  };

  return (
    <>
      <Box>
        {isLoadingSuppliers ? (
          <Skeleton />
        ) : (
          <Autocomplete
            options={suppliers}
            getOptionLabel={(option) =>
              `${option.supplierCode} - ${option.supplierName}` || ""
            }
            renderInput={(params) => (
              <TextField {...params} variant="outlined" label={t("supplier")} />
            )}
            renderOption={(props, item) => (
              <li {...props} key={item.supplierCode}>
                <ListItemText>{`${item.supplierCode} - ${item.supplierName}`}</ListItemText>
              </li>
            )}
            value={supplier}
            onChange={(_event, newValue) => {
              setSupplier(newValue);
              setFieldglassId(undefined);
            }}
          />
        )}
      </Box>

      <Box
        display="flex"
        sx={{
          border: "1px solid #ccc",
          padding: "20px",
          borderRadius: "5px",
          display: "flex",
          gap: "20px",
        }}
        justifyContent="space-around"
        marginY={5}
      >
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <MuiDatePicker
            label={t("start-date")}
            views={["year", "month"]}
            sx={{ width: "100%" }}
            value={initialDate}
            onChange={(date) => {
              setInitialDate(date);
              setEndDate(undefined);
            }}
            disabled={loading}
          />

          <MuiDatePicker
            label={t("end-date")}
            sx={{ width: "100%" }}
            views={["year", "month"]}
            value={endDate}
            onChange={(date) => {
              setEndDate(date);
            }}
            disabled={loading || !initialDate}
            minDate={initialDate}
            maxDate={addMonths(initialDate, 3)}
          />
        </LocalizationProvider>
      </Box>

      {!!supplier && (
        <Box key={supplier}>
          <FormControl fullWidth variant="outlined" margin="normal">
            <InputLabel id="select-option-label">
              {t("select-option")}
            </InputLabel>
            <Select
              labelId="select-option-label"
              id="select-option"
              value={selectedOption}
              onChange={handleRadioChange}
              label={t("select-option")}
            >
              <MenuItem value="both">{t("all-documents")}</MenuItem>
              <MenuItem value="sows">{t("sows")}</MenuItem>
              <MenuItem value="workers">{t("workers")}</MenuItem>
            </Select>
          </FormControl>

          <Box marginY={5}>
            {selectedOption === "sows" ? (
              isSowsLoading ? (
                <Skeleton />
              ) : (
                <>
                  <Autocomplete
                    options={Sows}
                    getOptionLabel={(option) =>
                      `${option.sowID} - ${option.sowName}` || ""
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        label={t("sow")}
                      />
                    )}
                    renderOption={(props, item) => (
                      <li {...props} key={item.sowID}>
                        <ListItemText>{`${item.sowID} - ${item.sowName}`}</ListItemText>
                      </li>
                    )}
                    key={fieldglassId}
                    value={fieldglassId}
                    onChange={(_event, newValue) => setFieldglassId(newValue)}
                  />
                  {fieldglassId && (
                    <FormGroup>
                      <FormControlLabel
                        control={
                          <Checkbox
                            onChange={(event) =>
                              setWorkersFromSow(event.target.checked)
                            }
                            checked={workersFromSow}
                            inputProps={{ "aria-label": "controlled" }}
                          />
                        }
                        label={t("get-workers-from-sow")}
                      />
                    </FormGroup>
                  )}
                </>
              )
            ) : selectedOption === "workers" ? (
              isWorkersLoading ? (
                <Skeleton />
              ) : (
                <Autocomplete
                  options={Workers}
                  getOptionLabel={(option) =>
                    `${option.workerID} - ${option.firstName} ${option.lastName}` ||
                    ""
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label={t("worker")}
                    />
                  )}
                  renderOption={(props, item) => (
                    <li {...props} key={item.workerID}>
                      <ListItemText>{`${item.workerID} - ${item.firstName} ${item.lastName}`}</ListItemText>
                    </li>
                  )}
                  key={fieldglassId}
                  value={fieldglassId}
                  onChange={(_event, newValue) => setFieldglassId(newValue)}
                />
              )
            ) : (
              <></>
            )}
          </Box>
          <ButtonTS
            color="primary"
            onClick={handleSendButton}
            disabled={loading || (selectedOption !== "both" && !fieldglassId)}
            loading={loading || isLoadingReport}
          >
            {t("generate-report")}
          </ButtonTS>
        </Box>
      )}
    </>
  );
}